import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banner, Button, DataTable, EmptyState, exportRows, QuickFilters, Tag, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkColSet, CkDataColumn, CkMenuItem, CkSelection, CkTableView } from "@NP-Webber/crm-kit/dist/next/index.js";
import { salesApi, type LeadStage, type ListenDto, type ListensPage as ListensData } from "../../api/salesApi";
import { ScreenHeader } from "../page/ScreenHeader";
import { PeriodBar } from "../PeriodBar";
import { periodText, type Period } from "../periods";
import { useShell } from "../shell/ShellContext";
import { useServerTable, type EnumColumn } from "../useServerTable";
import { copyText, errorMessage, fmtNum, fmtSecs, STAGE_META, todayIso } from "./salesUi";
import "./sales.css";

const SOURCE_LABEL: Record<string, string> = { log: "קובץ לוג", webhook: "Webhook" };
const ALL = "__all";

/**
 * /sales/listens — «שמיעות בקו»: who listened to which extension of the Yemot line, and for how long.
 * Turbo DataTable anatomy: extension cards as quick filters, period bar, typed columns with column filters,
 * views, open the lead / «יצירת לידים מהבחירה», export.
 */
export function ListensPage() {
  const navigate = useNavigate();
  const toast = useCkToast();
  const { allowed } = useShell();
  const canSetup = allowed("settings.integrations.yemot.manage");
  const canEdit = allowed("sales.edit");
  const [path, setPath] = useState("");
  const [period, setPeriod] = useState<Period>({ from: "", to: "" });
  const [clearKey, setClearKey] = useState(0);

  const enums = useMemo<Record<string, EnumColumn>>(
    () => ({
      source: { label: (v) => SOURCE_LABEL[v] ?? v, options: Object.entries(SOURCE_LABEL).map(([value, l]) => ({ value, label: l })) },
      leadStage: { label: (v) => STAGE_META[v as LeadStage]?.label ?? v },
    }),
    [],
  );
  const extra = useMemo(() => {
    const e: Record<string, string> = {};
    if (path) e.path = path;
    if (period.from) e.from = period.from;
    if (period.to) e.to = period.to;
    return e;
  }, [path, period]);

  const table = useServerTable<ListenDto, ListensData>({
    load: (p) => salesApi.listens(p),
    loadValues: (p) => salesApi.listensColumnValues(p),
    extra,
    enums,
    bools: ["hasLead"],
    errorText: "לא הצלחנו לטעון את השמיעות",
  });
  const { query, setQuery, firstPage, data, reload } = table;

  const changePath = (k: string) => {
    setPath(k === ALL ? "" : k);
    firstPage();
  };
  const changePeriod = (p: Period) => {
    setPeriod(p);
    firstPage();
  };

  const columns = useMemo<Array<CkDataColumn<ListenDto>>>(
    () => [
      { key: "startedAt", label: "תאריך ושעה", type: "date", sortable: true, render: (r) => new Date(r.startedAt).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" }) },
      { key: "phone", label: "טלפון", type: "text", ltr: true, copy: true, onCopied: () => toast("הועתק"), getValue: (r) => r.phone ?? "" },
      { key: "extension", label: "שלוחה", type: "enum", sortable: true, getValue: (r) => r.extensionLabel || r.extensionPath },
      { key: "extensionPath", label: "נתיב שלוחה", type: "enum", sortable: true, ltr: true },
      { key: "seconds", label: "משך (שניות)", type: "number", sortable: true, render: (r) => <span className="sl-num">{fmtSecs(r.seconds)}</span> },
      { key: "source", label: "מקור", type: "enum", sortable: true, getValue: (r) => SOURCE_LABEL[r.source] ?? r.source },
      { key: "hasLead", label: "יש ליד", type: "bool", getValue: (r) => Boolean(r.leadId) },
      { key: "leadName", label: "ליד", type: "text", sortable: true, getValue: (r) => r.leadName ?? "", render: (r) => (r.leadId ? <b>{r.leadName || "לליד"}</b> : <span className="sl-muted">—</span>) },
      {
        key: "leadStage",
        label: "שלב הליד",
        type: "enum",
        sortable: true,
        getValue: (r) => (r.leadStage ? STAGE_META[r.leadStage]?.label ?? r.leadStage : ""),
        render: (r) => (r.leadStage ? <Tag tone={STAGE_META[r.leadStage]?.tone ?? "neutral"}>{STAGE_META[r.leadStage]?.label ?? r.leadStage}</Tag> : ""),
      },
    ],
    [toast],
  );
  const colSets: CkColSet[] = [
    { key: "basic", label: "בסיסי", cols: ["startedAt", "phone", "extension", "seconds", "leadName"] },
    { key: "lead", label: "ליד", cols: ["startedAt", "phone", "hasLead", "leadName", "leadStage"] },
    { key: "tech", label: "טכני", cols: ["startedAt", "phone", "extensionPath", "seconds", "source"] },
  ];
  const periodState = { from: period.from, to: period.to, path };
  const views: CkTableView[] = [
    { id: "all", label: "כל השמיעות", state: { query: { search: "", filters: {}, sort: null }, extra: periodState } },
    { id: "long", label: "הקשבות ארוכות", state: { query: { search: "", filters: { seconds: { mode: "cond", op: "gte", v1: "300" } }, sort: { key: "seconds", dir: -1 } }, extra: periodState } },
    { id: "nolead", label: "בלי ליד", state: { query: { search: "", filters: { hasLead: { mode: "values", values: ["לא"] } }, sort: null }, extra: periodState } },
  ];

  const createLeads = async (ids: string[]) => {
    try {
      const r = await salesApi.leadsFromListens(ids);
      const parts = [r.created ? `${fmtNum(r.created)} לידים חדשים` : null, r.attached ? `${fmtNum(r.attached)} צורפו ללידים קיימים` : null, r.skipped ? `${fmtNum(r.skipped)} כבר עם ליד` : null].filter(Boolean);
      toast.success(parts.join(" · ") || "אין שמיעות לטיפול");
      setClearKey((k) => k + 1);
      reload();
    } catch (e) {
      toast.error(errorMessage(e, "יצירת הלידים נכשלה"));
    }
  };

  const rowMenu = (r: ListenDto): CkMenuItem[] => {
    const items: CkMenuItem[] = [];
    if (r.leadId) items.push({ key: "lead", label: "פתיחת הליד", icon: "expand", onClick: () => navigate(`/sales/leads/${r.leadId}`) });
    else if (canEdit) items.push({ key: "create", label: "יצירת ליד", icon: "plus", onClick: () => void createLeads([r.id]) });
    if (r.phone) {
      items.push({
        key: "copy",
        label: "העתקת טלפון",
        icon: "copy",
        onClick: () => void copyText(r.phone ?? "").then((ok) => (ok ? toast("הועתק") : toast.error("ההעתקה נחסמה בדפדפן"))),
      });
    }
    return items;
  };

  const byExt = data?.byExtension ?? [];
  const quickItems = [
    { key: ALL, label: "כל השלוחות", icon: "mic", tone: "primary" as const, value: byExt.reduce((a, x) => a + x.listens, 0) || (data ? 0 : undefined) },
    ...byExt.map((x) => ({ key: x.path, label: x.label || x.path, icon: "mic", tone: "info" as const, value: x.listens, sub: `${fmtNum(x.phones)} טלפונים שונים` })),
  ];
  const extName = byExt.find((x) => x.path === path)?.label || path;
  const extraChips = [
    path ? { key: "path", label: `שלוחה: ${extName}`, icon: "mic", tone: "primary" as const, onRemove: () => changePath(ALL) } : null,
    period.from || period.to ? { key: "period", label: `תקופה: ${periodText(period)}`, icon: "calendar", tone: "primary" as const, onRemove: () => changePeriod({ from: "", to: "" }) } : null,
  ].filter((x): x is NonNullable<typeof x> => Boolean(x));

  const filtered = Boolean(path || period.from || period.to || query.search || Object.keys(query.filters).length);
  const nothing = data && !data.total && !filtered && !byExt.length;
  const filename = `שמיעות-${todayIso()}`;

  return (
    <div className="sl-page">
      <ScreenHeader
        sub="נתוני שמיעה מקו ימות המשיח — מי הקשיב לאיזו שלוחה ולכמה זמן"
        more={canSetup ? [{ key: "setup", label: "הגדרות החיבור לקו", icon: "gear", onClick: () => navigate("/settings?section=yemot") }] : undefined}
      />
      {nothing ? (
        <EmptyState
          icon="mic"
          title="עדיין אין שמיעות"
          action={
            canSetup ? (
              <Button variant="primary" icon="gear" onClick={() => navigate("/settings?section=yemot")}>
                חיבור קו ימות המשיח
              </Button>
            ) : undefined
          }
        >
          {canSetup ? "חברו את הקו ובחרו אילו שלוחות לעקוב — מי שמקשיב ייכנס לכאן ולרשימת הלידים." : "עדיין לא נמשכו נתוני שמיעה מהקו. פנו למנהל המערכת כדי לחבר אותו."}
        </EmptyState>
      ) : (
        <>
          <PeriodBar value={period} onChange={changePeriod} />
          {byExt.length ? <QuickFilters items={quickItems} value={path || ALL} onChange={changePath} loading={table.loading && !data} title="שלוחות" hint="לחיצה על שלוחה מסננת את הטבלה" /> : null}
          {table.error && data ? <Banner tone="danger">{table.error}</Banner> : null}
          <DataTable<ListenDto>
            mode="server"
            columns={columns}
            rows={table.rows}
            total={table.total}
            loading={table.loading}
            error={data ? null : table.error}
            onRetry={reload}
            onRefresh={reload}
            query={query}
            onQueryChange={setQuery}
            storageKey="tb.sales.listens"
            layoutDefaults={{ hidden: ["extensionPath", "source", "hasLead", "leadStage"] }}
            colSets={colSets}
            getValueOptions={table.getValueOptions}
            searchPlaceholder="חיפוש טלפון…"
            extraChips={extraChips}
            onClearAll={() => {
              changePath(ALL);
              changePeriod({ from: "", to: "" });
            }}
            builtinViews={views}
            getExtraViewState={() => periodState}
            onApplyView={(x) => {
              const v = (x ?? {}) as Partial<Period> & { path?: string };
              setPeriod({ from: v.from ?? "", to: v.to ?? "" });
              setPath(v.path ?? "");
            }}
            onRowClick={(r) => (r.leadId ? navigate(`/sales/leads/${r.leadId}`) : undefined)}
            rowActions={(r) =>
              r.leadId
                ? [{ key: "lead", icon: "expand", label: "פתיחת הליד", onClick: () => navigate(`/sales/leads/${r.leadId}`) }]
                : canEdit
                  ? [{ key: "create", icon: "plus", label: "יצירת ליד", onClick: () => void createLeads([r.id]) }]
                  : []
            }
            rowMenu={rowMenu}
            bulkActions={
              canEdit
                ? [{ key: "leads", label: "יצירת לידים מהבחירה", icon: "plus", onClick: async (sel: CkSelection) => createLeads(await table.resolveIds(sel)) }]
                : []
            }
            clearSelectionKey={clearKey}
            exportFilename={filename}
            onExport={async ({ format, columns: cols, selection }) => {
              const list = await table.resolveRows(selection);
              await exportRows(list, cols, { format, filename });
              toast.success(`יוצאו ${fmtNum(list.length)} שורות`);
            }}
            emptyTitle="אין שמיעות שמתאימות לסינון"
            emptyText="נסו תקופה אחרת, שלוחה אחרת או נקו את הסינון."
            renderMobileRow={(r) => (
              <>
                <b className="sl-ltr">{r.phone || "—"}</b>
                <small>{[r.extensionLabel || r.extensionPath, fmtSecs(r.seconds), new Date(r.startedAt).toLocaleDateString("he-IL")].join(" · ")}</small>
              </>
            )}
          />
        </>
      )}
    </div>
  );
}

export default ListensPage;
